import { useAuth0 } from '@auth0/auth0-react';
import { ArrowLeft, User, Mail, Phone, Calendar, Shield, Edit3, Save, X } from 'lucide-react';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { useAuditLog } from '../hooks/useAuditLog';
import { TwoFactorSettings } from './TwoFactorSettings';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface ProfileProps {
  onBack: () => void;
}

export function Profile({ onBack }: ProfileProps) {
  const { user } = useAuth0();
  const { admin, adminStatus, refreshStatus } = useAdminStatus();
  const { logAction } = useAuditLog();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    mobilenumber: admin?.mobilenumber || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [hasLoggedView, setHasLoggedView] = useState(false);

  // Extract first and last names from Auth0 user data
  const firstname = user?.given_name || user?.name?.split(' ')[0] || '';
  const lastname = user?.family_name || user?.name?.split(' ').slice(1).join(' ') || '';

  // Update edit data when admin data changes
  useEffect(() => {
    if (admin) {
      setEditData({
        mobilenumber: admin.mobilenumber || ''
      });
    }
  }, [admin]);

  // Clear notifications after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Log profile viewing
  useEffect(() => {
    if (admin && !hasLoggedView) {
      logAction(
        'view_admin_profile',
        'admin_management',
        {
          resourceType: 'admin',
          resourceId: admin.id.toString(),
          details: {
            own_profile: true
          },
          success: true
        }
      );
      setHasLoggedView(true);
    }
  }, [admin, hasLoggedView, logAction]);

  const getStatusBadge = () => {
    switch (adminStatus) {
      case 'APPROVED':
        return (
          <div className="inline-flex items-center space-x-2 bg-green-500/20 text-green-500 dark:text-green-400 px-4 py-2 rounded-full border border-green-500/30">
            <Shield className="w-5 h-5" />
            <span className="text-base font-medium">APPROVED</span>
          </div>
        );
      case 'PENDING':
        return (
          <div className="inline-flex items-center space-x-2 bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 px-4 py-2 rounded-full border border-yellow-500/30">
            <Shield className="w-5 h-5" />
            <span className="text-base font-medium">PENDING</span>
          </div>
        );
      case 'DENIED':
        return (
          <div className="inline-flex items-center space-x-2 bg-red-500/20 text-red-500 dark:text-red-400 px-4 py-2 rounded-full border border-red-500/30">
            <Shield className="w-5 h-5" />
            <span className="text-base font-medium">DENIED</span>
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center space-x-2 bg-slate-500/20 text-slate-500 dark:text-slate-400 px-4 py-2 rounded-full border border-slate-500/30">
            <Shield className="w-5 h-5" />
            <span className="text-base font-medium">NOT REGISTERED</span>
          </div>
        );
    }
  };

  const handleSave = async () => {
    if (!admin) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('admins')
        .update({
          mobilenumber: editData.mobilenumber
        })
        .eq('id', admin.id);

      if (error) throw error;

      await refreshStatus();
      setIsEditing(false);
      setNotification({ message: 'Profile updated successfully', type: 'success' });

      // Log the profile edit action
      await logAction(
        'edit_admin_profile',
        'admin_management',
        {
          resourceType: 'admin',
          resourceId: admin.id.toString(),
          details: {
            previous_mobile: admin.mobilenumber,
            new_mobile: editData.mobilenumber,
            own_profile: true
          },
          success: true
        }
      );
    } catch (error) {
      console.error('Error updating profile:', error);
      setNotification({ message: 'Failed to update profile', type: 'error' });

      // Log the failed profile edit action
      await logAction(
        'edit_admin_profile',
        'admin_management',
        {
          resourceType: 'admin',
          resourceId: admin.id.toString(),
          details: {
            previous_mobile: admin.mobilenumber,
            new_mobile: editData.mobilenumber,
            own_profile: true
          },
          success: false,
          errorMessage: error instanceof Error ? error.message : 'Unknown error'
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      mobilenumber: admin?.mobilenumber || ''
    });
    setIsEditing(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          {notification.message}
        </div>
      )}
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Profile</h1>
          </div>
          
          {admin && adminStatus === 'APPROVED' && (
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="flex items-center space-x-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>{isSaving ? 'Saving...' : 'Save'}</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name || 'User'} 
                      className="w-24 h-24 rounded-2xl object-cover"
                    />
                  ) : (
                    <User className="w-12 h-12 text-white" />
                  )}
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {firstname && lastname ? `${firstname} ${lastname}` : (user?.name || 'User')}
                </h2>
                {getStatusBadge()}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Account Information */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Account Information</h3>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      First Name
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <User className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-900 dark:text-white font-medium">
                        {firstname || 'Not provided'}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">From Google account</span>
                    </div>
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Last Name
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <User className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-900 dark:text-white font-medium">
                        {lastname || 'Not provided'}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">From Google account</span>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Email Address
                  </label>
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-900 dark:text-white font-medium">{user.email}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">Cannot be changed</span>
                  </div>
                </div>

                {/* Mobile Number */}
                {admin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Mobile Number
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editData.mobilenumber}
                        onChange={(e) => setEditData({ ...editData, mobilenumber: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900 dark:text-white"
                      />
                    ) : (
                      <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <Phone className="w-5 h-5 text-slate-400" />
                        <span className="text-slate-900 dark:text-white font-medium">{admin.mobilenumber}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Member Since */}
                {admin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Member Since
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-900 dark:text-white font-medium">
                        {new Date(admin.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Auth0 ID */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Auth0 ID
                  </label>
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <Shield className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-900 dark:text-white font-mono text-sm break-all">
                      {user.sub}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Information */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                {adminStatus === 'PENDING' && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-yellow-800 dark:text-yellow-200">
                      Your account is pending approval. You'll be notified once an administrator reviews your request.
                    </p>
                  </div>
                )}

                {adminStatus === 'DENIED' && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-red-800 dark:text-red-200">
                      Your account request has been denied. Please contact your administrator for more information.
                    </p>
                  </div>
                )}

                {adminStatus === 'NOT_FOUND' && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <p className="text-slate-700 dark:text-slate-300">
                      You haven't registered for admin access yet. Complete the registration form to request access.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Two-Factor Authentication Settings - Only show for approved users */}
            {adminStatus === 'APPROVED' && <TwoFactorSettings />}
          </div>
        </div>
      </div>
    </div>
  );
}
